import React, { useState, useEffect, useRef } from "react";
import "./ChatPage.css";
import "bootstrap/dist/css/bootstrap.min.css";
import Nav from "../../assets/svg/icon1.svg";
import maiLogo from "../../assets/svg/chat-bot/Logo.svg";
import setting from "../../assets/svg/chat-bot/setting.svg";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AppBar from "../../components/layout/AppBar";
import useSpeechToText from "../../hooks/useSpeechToText";
import usePrechat from "../../hooks/usePrechat";
import MessageList from "./components/MessageList";
import ChatInput from "./components/ChatInput";
import { apiRequest } from "../../services/api_services/ApiServices";

const ChatPagePreDef = () => {
  const navigate = useNavigate();

  useEffect(() => {
    applicationList();

    const savedConvoId = localStorage.getItem("conversationId");

    if (savedConvoId) {
      setConversationId(Number(savedConvoId));
      getChat(Number(savedConvoId));
    } else {
      getChat(0);
    }

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleChange = (event) => {
    setInputField(event.target.value);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        setStagedImage(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error("Please select a valid image");
    }
  };

  const apiUrl = process.env.REACT_APP_API_URL;
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by this browser."));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
              { headers: { "Accept-Language": "en" } },
            );
            const data = await res.json();
            const addr = data.address || {};
            // Build a readable string: suburb/neighbourhood + city/town/county
            const parts = [
              addr.suburb || addr.neighbourhood || addr.quarter,
              addr.city || addr.town || addr.village || addr.county,
              addr.state,
              addr.country,
            ].filter(Boolean);
            resolve(
              parts.join(", ") ||
                data.display_name ||
                `${latitude}, ${longitude}`,
            );
          } catch {
            // Fallback to coordinates if reverse geocoding fails
            resolve(`${latitude}, ${longitude}`);
          }
        },
        (err) => {
          reject(err);
        },
        { enableHighAccuracy: true, timeout: 10000 },
      );
    });
  };

  const startCamera = async (location) => {
    if (location) {
      setCurrentLocation(location);
    }
    setIsCameraActive(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      streamRef.current = stream;
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      console.error("Camera access error:", err);
      toast.error("Could not access camera. Please check permissions.");
      setIsCameraActive(false);
    }
  };

  const openCameraWithDelay = async () => {
    let location;
    try {
      toast.info("Getting your current location...");
      location = await getCurrentLocation();
      setCurrentLocation(location);
    } catch (err) {
      console.error("Location error:", err);
      toast.error(
        "Location access is required to open the camera. Please allow location access and try again.",
      );
      return; // Do NOT open camera if location is unavailable
    }

    startCamera(location);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const base64Image = canvas.toDataURL("image/png");

      const tempConvo = [...chatConversation];
      tempConvo.push({
        question: (
          <img
            src={base64Image}
            alt="Captured"
            style={{ maxWidth: "200px", borderRadius: "10px" }}
          />
        ),
        dateAt: new Date(),
      });
      setChatConversation(tempConvo);

      sendMessage(
        "",
        tempConvo,
        application,
        base64Image,
        currentLocation,
      ).then((res) => {
        if (res?.openCamera) openCameraWithDelay();
      });
      stopCamera();
    }
  };

  const {
    loading,
    answerLoading,
    convoList,
    id,
    setChatId,
    conversationId,
    setConversationId,
    sessionId,
    setSessionId,
    chatConversation,
    setChatConversation,
    application,
    setApplication,
    options,
    isNewAnswer,
    newChat,
    setNewChat,
    endRef,
    scrollToBottom,
    getConversation,
    getChat,
    applicationList,
    sendMessage,
    loadMoreConversations,
    hasMoreConversations,
    isLoadingMore,
    conversationListLoading,
    chatDataLoading,
  } = usePrechat();

  const [modelSelect, setModelSelect] = useState("");
  const [inputField, setInputField] = useState("");
  const [stagedImage, setStagedImage] = useState(null);
  const scrollContainerRef = useRef(null);
  const conversationsScrollRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;
    const isNotAtBottom = scrollHeight - scrollTop - clientHeight > 150;
    setShowScrollButton(isNotAtBottom);
  };

  const { isListening, startListening, stopListening } =
    useSpeechToText(setInputField);
  const [inputMessage, setInputMessage] = useState("");

  const modelOptions = [
    { value: "pre_defined", label: "Pre defined model" },
    { value: "application", label: "Application model" },
  ];

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingConvoId, setEditingConvoId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [deleteModalConvo, setDeleteModalConvo] = useState(null);

  // Setup scroll listener for conversation list lazy loading
  useEffect(() => {
    const conversationsScroll = conversationsScrollRef.current;
    if (!conversationsScroll) return;

    const handleConversationsScroll = () => {
      if (!conversationsScrollRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } =
        conversationsScrollRef.current;
      // Load more when user scrolls near the bottom (within 100px)
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      if (isNearBottom && hasMoreConversations && !isLoadingMore) {
        loadMoreConversations(application);
      }
    };

    conversationsScroll.addEventListener("scroll", handleConversationsScroll);
    return () => {
      conversationsScroll.removeEventListener(
        "scroll",
        handleConversationsScroll,
      );
    };
  }, [hasMoreConversations, isLoadingMore, application, loadMoreConversations]);

  const handleUpdateConversation = async (e, id) => {
    if (e) e.stopPropagation();
    if (!editingTitle.trim()) {
      setEditingConvoId(null);
      return;
    }
    try {
      await apiRequest({
        method: "put",
        url: "prechat/update_conversation",
        data: { convo_id: id, title: editingTitle },
      });
      toast.success("Conversation updated");
      applicationList();
      setEditingConvoId(null);
    } catch (err) {
      toast.error("Failed to update conversation");
    }
  };

  const confirmDeleteConversation = async () => {
    if (!deleteModalConvo) return;
    const { id } = deleteModalConvo;
    try {
      await apiRequest({
        method: "delete",
        url: "prechat/delete_conversation",
        data: { convo_id: id },
      });
      toast.success("Conversation deleted");
      applicationList();
      if (conversationId === id) {
        setConversationId(0);
        getChat(0);
        localStorage.removeItem("conversationId");
      }
    } catch (err) {
      toast.error("Failed to delete conversation");
    } finally {
      setDeleteModalConvo(null);
    }
  };

  const filteredConvoList = convoList.filter((convo) => {
    if (!searchQuery.trim()) return true;
    const title =
      convo.title && convo.title.startsWith("data:image/")
        ? "Captured Image"
        : convo.title || "";
    return title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleSubmitQuestion = () => {
    if (modelSelect === "selective model") {
      toast.warning("Please select model to chat!");
      return;
    }

    if (inputField.trim() === "" && !stagedImage) return;

    const tempConversation1 = [...chatConversation];
    const txt = inputField.trim();
    let textToSend = txt;

    if (stagedImage || txt !== "") {
      const entry = { dateAt: new Date() };
      if (stagedImage) entry.questionImage = stagedImage;
      if (txt !== "") entry.question = txt;
      tempConversation1.push(entry);
    }

    setChatConversation(tempConversation1);
    setInputField("");
    setStagedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    sendMessage(
      txt,
      tempConversation1,
      application,
      stagedImage || null,
      stagedImage ? "" : undefined,
    ).then((res) => {
      if (res?.openCamera) openCameraWithDelay();
    });
    scrollToBottom();
  };

  return (
    <div className="chat-container">
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* ── SIDEBAR ── */}
      <div className={`sidebar ${sidebarOpen ? "sidebar--open" : ""}`}>
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src={maiLogo} alt="MAI Logo" />
          </div>
          <button
            className="sidebar-settings-btn"
            onClick={() => navigate("/admin_setting")}
            title="Settings"
            type="button"
          >
            <img src={setting} alt="Settings" />
          </button>
        </div>

        {/* New Chat Button — full width, top */}
        <button
          className="new-chat-btn"
          type="button"
          onClick={() => {
            if (modelSelect === "selective model") {
              toast.warning("Please select model to chat!");
            } else {
              getChat(0);
              setNewChat("");
              getConversation(application);
              setSidebarOpen(false);
            }
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          New chat
        </button>

        {/* Search */}
        <div className="sidebar-search-wrap">
          <input
            className="sidebar-search"
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Conversations label */}
        <div className="sidebar-conversations-label">Conversations</div>

        {/* Mobile close button */}
        <button
          className="close-sidebar-btn mobile-only"
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "absolute",
            top: "16px",
            right: "12px",
            background: "none",
            border: "none",
            fontSize: "22px",
            cursor: "pointer",
            color: "#66737E",
          }}
          type="button"
        >
          &times;
        </button>

        {/* Conversation List Loading State */}
        {conversationListLoading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "500px",
              color: "#999",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #3498db",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto 10px",
                }}
              ></div>
              <p style={{ margin: 0, fontSize: "12px" }}>
                Loading conversations...
              </p>
            </div>
          </div>
        )}

        <div
          className="sidebar-conversations-scroll"
          ref={conversationsScrollRef}
        >
          {filteredConvoList.length > 0 ? (
            filteredConvoList.map((convo, index) => {
              const title =
                convo.title && convo.title.startsWith("data:image/")
                  ? "Captured Image"
                  : convo.title;
              return (
                <div
                  key={index}
                  className={`conversation-item${convo.id === conversationId ? " active" : ""}`}
                  onClick={() => {
                    setConversationId(convo.id);
                    localStorage.setItem("conversationId", convo.id);
                    getChat(convo.id);
                    setSidebarOpen(false);
                  }}
                >
                  <div className="convo-item-left">
                    <img src={Nav} alt="chat" className="convo-item-icon" />
                    {editingConvoId === convo.id ? (
                      <input
                        type="text"
                        className="convo-item-edit-input"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleUpdateConversation(e, convo.id);
                          } else if (e.key === "Escape") {
                            setEditingConvoId(null);
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className="convo-item-text">{title}</span>
                    )}
                  </div>

                  <div className="convo-item-actions">
                    {editingConvoId === convo.id ? (
                      <button
                        className="convo-item-action-btn"
                        data-tooltip="Save"
                        type="button"
                        onClick={(e) => handleUpdateConversation(e, convo.id)}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </button>
                    ) : (
                      <>
                        <button
                          className="convo-item-action-btn edit-btn"
                          data-tooltip="Rename"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingTitle(title);
                            setEditingConvoId(convo.id);
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button
                          className="convo-item-action-btn delete-btn"
                          data-tooltip="Remove"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteModalConvo({ id: convo.id, title });
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          ) : !conversationListLoading ? (
            <p className="no-conversations">
              {searchQuery
                ? "No matching conversations"
                : "No conversations yet"}
            </p>
          ) : null}
          {isLoadingMore && (
            <div
              style={{
                padding: "12px",
                textAlign: "center",
                color: "#999",
                fontSize: "12px",
              }}
            >
              Loading more conversations...
            </div>
          )}
        </div>
      </div>

      <div
        className="chat-window"
        style={{
          backgroundColor: "#FFFFFF",
          width: "100%",
          overflowY: "hidden",
        }}
      >
        <div className="d-flex flex-column" style={{ flexShrink: 0 }}>
          <div
            className="d-flex align-items-center bg-white"
            style={{ height: "50px" }}
          >
            <button
              className="hamburger-btn"
              onClick={() => setSidebarOpen(true)}
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                marginLeft: "15px",
              }}
            >
              &#9776;
            </button>
            <AppBar classNameAppBar="flex-grow-1 bg-white h-[40px]" />
          </div>
        </div>

        <div
          className="chat-messages-scroll"
          ref={scrollContainerRef}
          onScroll={handleScroll}
          style={{ overflowY: "auto", flex: 1, padding: "0 20px" }}
        >
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            {chatDataLoading && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "60px 20px",
                  minHeight: "600px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      border: "4px solid #f3f3f3",
                      borderTop: "4px solid #3498db",
                      borderRadius: "50%",
                      width: "50px",
                      height: "50px",
                      animation: "spin 1s linear infinite",
                      margin: "0 auto 15px",
                    }}
                  ></div>
                  <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
                    Loading conversation...
                  </p>
                </div>
              </div>
            )}
            {!chatDataLoading && (
              <MessageList
                chatConversation={chatConversation}
                isNewAnswer={isNewAnswer}
                answerLoading={answerLoading}
                endRef={endRef}
                modelSelect={modelSelect}
                onSuggestedPrompt={(prompt) => {
                  setInputField(prompt);
                }}
              />
            )}
          </div>{" "}
        </div>
        <div style={{ position: "relative" }}>
          {showScrollButton && (
            <button
              onClick={() => {
                scrollToBottom();
                setShowScrollButton(false);
              }}
              style={{
                position: "absolute",
                bottom: "100%",
                left: "50%",
                transform: "translateX(-50%) translateY(-10px)",
                background: "#ffffff",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 100,
                color: "#475569",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#0066CC";
                e.currentTarget.style.transform =
                  "translateX(-50%) translateY(-12px)";
                e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#475569";
                e.currentTarget.style.transform =
                  "translateX(-50%) translateY(-10px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
              }}
              title="Scroll to bottom"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <polyline points="19 12 12 19 5 12"></polyline>
              </svg>
            </button>
          )}
          <ChatInput
            inputField={inputField}
            handleChange={(e) => setInputField(e.target.value)}
            handleSubmitQuestion={handleSubmitQuestion}
            startCamera={startCamera}
            fileInputRef={fileInputRef}
            handleImageUpload={handleImageUpload}
            stagedImage={stagedImage}
            clearStagedImage={() => {
              setStagedImage(null);
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
            }}
          />
        </div>
      </div>
      {isCameraActive && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            zIndex: 99999,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "90%",
              maxWidth: "500px",
              background: "white",
              borderRadius: "20px",
              overflow: "hidden",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            }}
          >
            <h4
              style={{
                marginBottom: "15px",
                fontWeight: "600",
                fontFamily: "Omny, sans-serif",
                color: "#091E42",
              }}
            >
              Capture Photo
            </h4>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                width: "100%",
                borderRadius: "12px",
                backgroundColor: "black",
                transform: "scaleX(-1)",
              }}
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />

            <div
              style={{
                display: "flex",
                gap: "15px",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <button
                onClick={stopCamera}
                style={{
                  flex: 1,
                  padding: "10px 0",
                  borderRadius: "100px",
                  border: "1px solid #E2E8F0",
                  background: "#F8FAFC",
                  color: "#475569",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={captureImage}
                style={{
                  flex: 1,
                  padding: "10px 0",
                  borderRadius: "100px",
                  border: "none",
                  background:
                    "linear-gradient(135deg, #0066CC 0%, #007bff 70%)",
                  color: "white",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Capture
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModalConvo && (
        <div
          className="delete-modal-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteModalConvo(null);
          }}
        >
          <div
            className="delete-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-modal-header">
              <h3>Delete Conversation</h3>
            </div>
            <div className="delete-modal-body">
              Are you sure you want to delete "
              <strong>{deleteModalConvo.title}</strong>"?
            </div>
            <div className="delete-modal-footer">
              <button
                className="delete-modal-cancel"
                onClick={() => setDeleteModalConvo(null)}
              >
                Cancel
              </button>
              <button
                className="delete-modal-confirm"
                onClick={confirmDeleteConversation}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPagePreDef;
